import { ArrowUpRight, Check } from "lucide-react";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Reveal from "./Reveal";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import { ORIG } from "../mock";

const sports = {
  intro: "Sport is a great way for children to learn essential life skills — teamwork, discipline, resilience and the joy of effort. Our state-of-the-art sports facilities give every child the chance to play, train and compete at the level that's right for them.",
  facilities: [
    "60,000 sq.ft. air-conditioned indoor sports complex",
    "Olympic-standard cricket field with practice nets",
    "Heated semi-Olympic swimming pool",
    "Indoor tennis, badminton & squash courts",
    "Equestrian, archery and athletics",
    "Yoga, fitness centre and dedicated coaching",
  ],
  programs: [
    { title: "Cricket", body: "Coached by professionals on an Olympic-standard ground with nets and floodlights." },
    { title: "Swimming", body: "Year-round programmes in our heated semi-Olympic pool, from beginner to competitive." },
    { title: "Equestrian", body: "Our equestrian programme builds confidence, posture and discipline on horseback." },
    { title: "Tennis & Squash", body: "Indoor and outdoor courts with structured coaching across age groups." },
    { title: "Athletics", body: "Track, field and conditioning programmes that nurture champions." },
    { title: "Yoga & Fitness", body: "Daily yoga and structured fitness sessions keep mind and body in harmony." },
  ],
  images: [
    `${ORIG}/banner-sport.jpg`,
    `${ORIG}/banner-sport1.jpg`,
    `${ORIG}/banner-sport2.jpg`,
  ],
};

export default function SportsPage() {
  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero
            eyebrow="Beyond Academics"
            title="Sport"
            italic="— life skills on the field."
            image={sports.images[0]}
            blurb={sports.intro}
          />

          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-12">
                  <div className="eyebrow mb-4">Sports Programmes</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Programmes for
                    <span className="serif-italic gold-text"> every passion.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {sports.programs.map((p, i) => (
                  <Reveal key={p.title} delay={(i % 3) * 100}>
                    <div className="p-6 light-card h-full">
                      <div className="eyebrow mb-3">0{i + 1}</div>
                      <h3 className="serif text-[24px] md:text-[28px] text-[var(--ink)] leading-tight">
                        {p.title}
                      </h3>
                      <span className="gold-divider-thin block my-4" />
                      <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75">{p.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: "var(--navy-deep)" }}>
            <div className="mandala-bg" style={{ opacity: 0.35 }} />
            <div className="relative max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-12">
                  <div className="eyebrow mb-4">Facilities</div>
                  <h2 className="h-section text-white">
                    World-class
                    <span className="serif-italic gold-text"> sports infrastructure.</span>
                  </h2>
                  <span className="gold-divider mx-auto" />
                </div>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                {sports.facilities.map((f, i) => (
                  <Reveal key={f} delay={(i % 2) * 100}>
                    <div className="flex items-start gap-3 border-b border-[rgba(184,148,86,0.2)] pb-4">
                      <Check size={18} className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.5} />
                      <span className="serif text-white/85 text-[16px] md:text-[17px] leading-snug">{f}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal>
                <div className="mt-12 md:mt-14 text-center">
                  <button onClick={openEnquire} className="btn-gold inline-flex items-center gap-2" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Enquire about Sports <ArrowUpRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          <PullQuote
            text="The field, the court, the pool — these are where character is quietly built, one heartbeat at a time."
          />

          <RelatedPages current="/sports.html" />
        </>
      )}
    </PageShell>
  );
}
